/** Adds a withdrawal address to the whitelist (needs a 2FA code; a cooldown may apply on mainnet). */
import type { WhitelistEntry } from "@custos/api-client";
import { Button, Field, Input, Modal, Notice, Select } from "@custos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { addressPlaceholder, findChain, looksLikeAddress, networkLabel } from "../lib/chains";
import { errorMessage } from "../lib/errors";
import { qk, useConfig } from "../lib/queries";
import { CodeInput } from "./bits";

export function AddAddressModal({
  open,
  onOpenChange,
  defaultChain,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultChain?: string;
  onAdded?: (entry: WhitelistEntry) => void;
}) {
  const config = useConfig();
  const queryClient = useQueryClient();
  const chains = config.data?.chains ?? [];
  const [chain, setChain] = useState(defaultChain ?? "");
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setChain(defaultChain ?? chains[0]?.key ?? "");
      setAddress("");
      setLabel("");
      setCode("");
      setTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultChain]);

  const selected = findChain(chains, chain);
  const mode = config.data?.networkMode ?? "testnet";
  const formatOk = selected ? looksLikeAddress(selected.family, address, mode) : false;
  const addressError =
    touched && address.trim() && !formatOk && selected
      ? `This doesn't look like a ${networkLabel(selected)} address. Check you picked the right network.`
      : null;

  const add = useMutation({
    mutationFn: () =>
      api.addWhitelist({
        chain,
        address: address.trim(),
        totpCode: code,
        ...(label.trim() ? { label: label.trim() } : {}),
      }),
    onSuccess: async (entry) => {
      await queryClient.invalidateQueries({ queryKey: qk.whitelist });
      onAdded?.(entry);
      onOpenChange(false);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!formatOk || code.length !== 6) return;
    add.mutate();
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add withdrawal address"
      description="Withdrawals can only go to saved addresses."
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Network">
          {(p) => (
            <Select {...p} value={chain} onChange={(e) => setChain(e.target.value)}>
              {chains.map((c) => (
                <option key={c.key} value={c.key}>
                  {networkLabel(c)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Address"
          error={addressError}
          hint={selected ? `Only a ${networkLabel(selected)} address.` : undefined}
        >
          {(p) => (
            <Input
              {...p}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={selected ? addressPlaceholder(selected.family, mode) : ""}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          )}
        </Field>
        <Field label="Label (optional)">
          {(p) => (
            <Input
              {...p}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={60}
              placeholder="e.g. My MetaMask"
            />
          )}
        </Field>
        <Field label="2FA code" hint="From your authenticator app.">
          {(p) => <CodeInput {...p} value={code} onChange={setCode} />}
        </Field>
        {mode === "mainnet" && (
          <Notice tone="warning">
            New addresses can be used after a waiting period (24 hours by default).
          </Notice>
        )}
        {add.error && <Notice tone="danger">{errorMessage(add.error)}</Notice>}
        <Button type="submit" block loading={add.isPending} disabled={!formatOk || code.length !== 6}>
          Save address
        </Button>
      </form>
    </Modal>
  );
}
